import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { Button, EmptyState, StatusBadge } from '@immo/ui-kit';
import type { ValuationAgentTokenView, ValuationAgentTokensResponse } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useCan } from '@/lib/auth/permissions';
import { valuationAgentPermissionLabelKey } from '@/lib/valuation-agent/permission-label';
import { useTranslations } from '@/i18n/use-translations';
import { KeyIcon } from '@/components/layout/icons';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { IssueTokenDialog } from '@/pages/valuation-agent/issue-token-dialog';
import { RevokeTokenDialog } from '@/pages/valuation-agent/revoke-token-dialog';

export async function valuationAgentLoader(): Promise<ValuationAgentTokensResponse> {
  const result = await apiClient.adminValuationAgentToken.get();

  if (!result.ok) {
    throw new Response('Failed to load the valuation agent token', { status: result.status });
  }

  return result.body;
}

type Dialog = 'issue' | 'replace' | 'revoke' | null;

export function ValuationAgentPage() {
  const t = useTranslations('pages.admin.valuation-agent');
  const { active, replaced } = useLoaderData() as ValuationAgentTokensResponse;
  const revalidator = useRevalidator();
  const canManage = useCan('backoffice:identity:valuation-agent-token-manage');
  const [dialog, setDialog] = useState<Dialog>(null);

  const revalidate = () => {
    void revalidator.revalidate();
  };

  return (
    <>
      <PageTopbar
        leading={<h1 className="truncate text-base font-semibold text-text-primary">{t('title')}</h1>}
        trailing={
          canManage && active !== null ? (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => setDialog('replace')}>
                {t('replace')}
              </Button>
              <Button size="sm" variant="danger-quiet" onClick={() => setDialog('revoke')}>
                {t('revoke')}
              </Button>
            </div>
          ) : undefined
        }
      />
      <div className="bg-canvas p-4">
        {active === null ? (
          <EmptyState
            icon={<KeyIcon className="h-8 w-8" aria-hidden />}
            title={t('empty')}
            action={canManage ? <Button onClick={() => setDialog('issue')}>{t('issue')}</Button> : undefined}
          />
        ) : (
          <div className="flex max-w-[880px] flex-col gap-4">
            <TokenCard token={active} />
            {replaced !== null && <TokenCard token={replaced} />}
          </div>
        )}
      </div>
      {dialog === 'issue' && (
        <IssueTokenDialog
          onClose={() => {
            setDialog(null);
            revalidate();
          }}
        />
      )}
      {dialog === 'replace' && active !== null && (
        <IssueTokenDialog
          replacing={active}
          onClose={() => {
            setDialog(null);
            revalidate();
          }}
        />
      )}
      {dialog === 'revoke' && (
        <RevokeTokenDialog
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            revalidate();
          }}
        />
      )}
    </>
  );
}

function TokenCard({ token }: { readonly token: ValuationAgentTokenView }) {
  const t = useTranslations('pages.admin.valuation-agent');
  const intl = useIntl();
  const date = intl.formatDate(token.issuedAt, { dateStyle: 'medium' });
  const isReplaced = token.status === 'REPLACED';

  return (
    <section
      className={`divide-y divide-border rounded-lg border border-border ${isReplaced ? 'bg-canvas' : 'bg-surface'}`}
    >
      <div className="flex flex-wrap items-center gap-3 px-5 py-4">
        {isReplaced ? (
          <StatusBadge tone="warning" dot label={t('status.replaced')} />
        ) : (
          <StatusBadge tone="success" dot label={t('status.active')} />
        )}
        <p className="text-base-sm text-text-secondary">
          {token.issuedBy.name === null
            ? t('issued-at', { date })
            : t('issued-at-by', { date, name: token.issuedBy.name })}
        </p>
        {isReplaced && token.validUntil !== null && (
          <p className="basis-full text-base-sm font-medium text-warning-700">
            {t('valid-until', {
              validUntil: intl.formatDate(token.validUntil, { dateStyle: 'medium', timeStyle: 'short' }),
            })}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-2 px-5 py-4">
        <p className="text-base-sm font-medium text-text-primary">{t('permissions-label')}</p>
        <ul className="flex flex-wrap gap-2">
          {token.permissions.map((permission) => (
            <li key={permission}>
              <StatusBadge
                tone="neutral"
                shape="square"
                size="sm"
                flexible
                label={t(`permission.${valuationAgentPermissionLabelKey(permission)}`)}
              />
            </li>
          ))}
        </ul>
      </div>
      {token.lastUsedAt !== null && (
        <p className="px-5 py-4 text-base-sm text-text-secondary">
          {t('last-used', {
            dateTime: intl.formatDate(token.lastUsedAt, { dateStyle: 'medium', timeStyle: 'short' }),
          })}
        </p>
      )}
    </section>
  );
}
